/**
 * Hide sidebar entries that carry a `visibleTo` the current user is not in.
 *
 * This is a convenience, NOT a security boundary. The backend resolver is what
 * actually refuses; anyone who types the URL still reaches the page and gets an
 * error from the server. Do not add a route guard reading this same value and
 * call the result "protected" -- one gate, in one place, is what keeps that
 * honest.
 *
 * Pure on purpose: no Vue, no store, no imports. It can be tested without
 * mounting a layout, which is why it is a file of its own rather than a method
 * on Main.vue.
 *
 * @param {Array<object>} items navMenuItems, or a header group's `items`
 * @param {?string} userLogin the current user, undefined until the `me` query
 *   returns -- an unknown user sees no gated entry, never the other way round
 * @return {Array<object>} a new array; entries without `visibleTo` are passed
 *   through by reference, untouched
 */
export function filterMenuItems(items, userLogin) {
  if (!Array.isArray(items)) return items;

  return items.reduce((kept, item) => {
    if (Array.isArray(item.visibleTo) && !item.visibleTo.includes(userLogin)) {
      return kept;
    }
    // A header group holds its own list in `item.items`; recurse into that
    // one level. The real sidebar is three levels deep -- header group ->
    // item -> `submenu` -- and this only ever recurses on `items`, never on
    // `submenu`. A `visibleTo` placed on a submenu leaf is silently ignored
    // and shown to everyone; gate at the `items` level (as the vdo-migration
    // entry does) until submenu support is added on purpose.
    if (Array.isArray(item.items)) {
      const inner = filterMenuItems(item.items, userLogin);
      // Hand back the ORIGINAL object when nothing inside was removed. A
      // group that always came out as a fresh copy would make "every ungated
      // entry is exactly as it was" untestable by identity, and identity is
      // the cheapest way to prove this filter changed nothing it should not.
      if (inner.length === item.items.length) {
        kept.push(item);
        return kept;
      }
      // A group that HAD children but lost all of them to filtering leaves
      // nothing to show under its heading -- drop the group itself rather
      // than render a header with an empty submenu. A group that started
      // empty is left exactly as it was; that emptiness is not this
      // function's doing.
      if (item.items.length > 0 && inner.length === 0) {
        return kept;
      }
      kept.push({ ...item, items: inner });
      return kept;
    }
    kept.push(item);
    return kept;
  }, []);
}
